import { useState, useMemo } from 'react';

const RATE_PER_RESPONSE = 5;
const TAX_RATE = 0.06;

const PLATFORMS = [
  {
    id: 'avito',
    title: 'Авито',
    badge: 'avg 200/сут',
    desc: 'Авито — самый большой поток. В среднем 200 откликов в сутки.',
    icon: 'ri-store-2-line',
    accent: 'amber',
    avito: 200,
    zarplata: 0,
    days: 22,
  },
  {
    id: 'zarplata',
    title: 'Зарплата.ру',
    badge: 'avg 100/сут',
    desc: 'Зарплата.ру даёт дополнительно около 100 откликов в сутки.',
    icon: 'ri-briefcase-4-line',
    accent: 'emerald',
    avito: 0,
    zarplata: 100,
    days: 22,
  },
  {
    id: 'both',
    title: 'Авито + Зарплата.ру',
    badge: 'avg 300/сут',
    desc: '200 + 100 = 300 откликов в сутки. Максимальный заработок.',
    icon: 'ri-rocket-2-line',
    accent: 'orange',
    avito: 200,
    zarplata: 100,
    days: 22,
  },
  {
    id: 'start',
    title: 'Старт',
    badge: 'min',
    desc: 'Первая неделя — пока не раскрутились. Консервативный прогноз.',
    icon: 'ri-seedling-line',
    accent: 'teal',
    avito: 80,
    zarplata: 30,
    days: 7,
  },
];

const ACCENT_MAP: Record<string, { text: string; bg: string; border: string; ring: string }> = {
  amber:   { text: 'text-amber-400',   bg: 'bg-amber-400/10',   border: 'border-amber-400/40',   ring: 'ring-amber-400/30'   },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/40', ring: 'ring-emerald-400/30' },
  orange:  { text: 'text-orange-400',  bg: 'bg-orange-400/10',  border: 'border-orange-400/40',  ring: 'ring-orange-400/30'  },
  teal:    { text: 'text-teal-400',    bg: 'bg-teal-400/10',    border: 'border-teal-400/40',    ring: 'ring-teal-400/30'    },
};

const calcNet = (avito: number, zarplata: number, days: number) => {
  const total = (avito + zarplata) * days;
  const gross = total * RATE_PER_RESPONSE;
  const tax = Math.round(gross * TAX_RATE);
  return { total, gross, tax, net: gross - tax };
};

export default function HRSalaryCalculator() {
  const [avito, setAvito]     = useState(200);
  const [zarplata, setZarplata] = useState(100);
  const [days, setDays]       = useState(22);
  const [activePreset, setActivePreset] = useState<string | null>('both');

  const applyPreset = (p: typeof PLATFORMS[0]) => {
    setAvito(p.avito);
    setZarplata(p.zarplata);
    setDays(p.days);
    setActivePreset(p.id);
  };

  const handleAvito    = (v: number) => { setAvito(v);    setActivePreset(null); };
  const handleZarplata = (v: number) => { setZarplata(v); setActivePreset(null); };
  const handleDays     = (v: number) => { setDays(v);     setActivePreset(null); };

  const { total, gross, tax, net } = useMemo(
    () => calcNet(avito, zarplata, days),
    [avito, zarplata, days]
  );

  const perDay = days > 0 ? Math.round(net / days) : 0;

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Калькулятор заработной платы</h2>
        <p className="text-slate-500 text-sm">
          Ставка: <span className="text-amber-400 font-semibold">5 ₽ за отклик</span> · расчёт с учётом НПД 6%
        </p>
      </div>

      {/* Presets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {PLATFORMS.map((p) => {
          const a = ACCENT_MAP[p.accent];
          const isActive = activePreset === p.id;
          const est = calcNet(p.avito, p.zarplata, p.days);
          return (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              className={`relative text-left rounded-2xl border p-4 transition-all cursor-pointer
                ${isActive
                  ? `${a.bg} ${a.border} ring-1 ${a.ring}`
                  : 'bg-[#13161f] border-[#1e2130] hover:border-slate-600 hover:bg-[#15182280]'
                }`}
            >
              {isActive && (
                <span className={`absolute top-3 right-3 w-5 h-5 flex items-center justify-center ${a.text}`}>
                  <i className="ri-check-line text-sm" />
                </span>
              )}
              <span className={`w-8 h-8 flex items-center justify-center rounded-xl mb-3 ${a.bg}`}>
                <i className={`${p.icon} text-base ${a.text}`} />
              </span>
              <p className="text-sm font-semibold text-white leading-tight mb-1">{p.title}</p>
              <div className={`inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-2 ${a.bg} ${a.text}`}>
                {p.badge}
              </div>
              <p className="text-[11px] text-slate-500 mb-3 leading-snug">{p.desc}</p>
              <div className="border-t border-white/5 pt-3">
                <p className="text-[10px] text-slate-600 mb-0.5">Ожидаемый доход / мес</p>
                <p className={`text-sm font-bold ${a.text}`}>~{est.net.toLocaleString('ru-RU')} ₽</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Sliders + Result */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Sliders */}
        <div className="lg:col-span-3 bg-[#13161f] border border-[#1e2130] rounded-2xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">Параметры расчёта</h3>
            <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-500">
              {(avito + zarplata) * days} откликов / мес
            </span>
          </div>

          {/* Progress bar */}
          <div>
            <div className="h-2 bg-[#0d0f17] rounded-full overflow-hidden flex gap-0.5">
              <div
                className="h-full bg-amber-400 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((avito / 300) * 100, 100)}%` }}
              />
              <div
                className="h-full bg-emerald-400 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((zarplata / 300) * 100, 100 - Math.min((avito / 300) * 100, 100))}%` }}
              />
            </div>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-[11px] text-slate-500">Авито</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-[11px] text-slate-500">Зарплата.ру</span>
              </div>
            </div>
          </div>

          {/* Avito slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-400/10">
                  <i className="ri-store-2-line text-sm text-amber-400" />
                </span>
                <span className="text-sm text-slate-300">Авито (откликов/сут)</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-amber-400">{avito}</span>
                <span className="text-xs text-amber-600/70 ml-1">{(avito * days * RATE_PER_RESPONSE).toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>
            <input
              type="range" min={0} max={300} step={10} value={avito}
              onChange={(e) => handleAvito(Number(e.target.value))}
              className="w-full accent-amber-400 cursor-pointer h-1.5"
            />
            <div className="flex justify-between text-[11px] text-slate-700 mt-1.5">
              <span>0</span><span>150</span><span>300</span>
            </div>
          </div>

          {/* Zarplata slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-400/10">
                  <i className="ri-briefcase-4-line text-sm text-emerald-400" />
                </span>
                <span className="text-sm text-slate-300">Зарплата.ру (откликов/сут)</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-emerald-400">{zarplata}</span>
                <span className="text-xs text-emerald-400/70 ml-1">{(zarplata * days * RATE_PER_RESPONSE).toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>
            <input
              type="range" min={0} max={200} step={10} value={zarplata}
              onChange={(e) => handleZarplata(Number(e.target.value))}
              className="w-full accent-emerald-400 cursor-pointer h-1.5"
            />
            <div className="flex justify-between text-[11px] text-slate-700 mt-1.5">
              <span>0</span><span>100</span><span>200</span>
            </div>
          </div>

          {/* Days slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-400/10">
                  <i className="ri-calendar-line text-sm text-orange-400" />
                </span>
                <span className="text-sm text-slate-300">Рабочих дней</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-orange-400">{days}</span>
                <span className="text-xs text-slate-500">дн</span>
              </div>
            </div>
            <input
              type="range" min={1} max={31} step={1} value={days}
              onChange={(e) => handleDays(Number(e.target.value))}
              className="w-full accent-orange-400 cursor-pointer h-1.5"
            />
            <div className="flex justify-between text-[11px] text-slate-700 mt-1.5">
              <span>1</span><span>15</span><span>31</span>
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-2 bg-[#0c1a12] border border-emerald-900/30 rounded-2xl p-6 flex flex-col justify-between">
          <div>
            <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-widest mb-5">Прогноз выплаты</p>

            <div className="space-y-3 mb-5">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Всего откликов</span>
                <span className="text-sm font-semibold text-slate-300">{total.toLocaleString('ru-RU')} шт</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Ставка за отклик</span>
                <span className="text-sm font-semibold text-amber-400">{RATE_PER_RESPONSE} ₽</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Брутто</span>
                <span className="text-sm font-semibold text-slate-300">{gross.toLocaleString('ru-RU')} ₽</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">НПД 6%</span>
                <span className="text-sm font-semibold text-red-400">−{tax.toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>

            <div className="border-t border-emerald-900/30 pt-5 mb-5">
              <p className="text-xs text-emerald-700 mb-1.5">К выплате на руки</p>
              <div className="flex items-end gap-1.5">
                <span className="text-4xl font-bold text-emerald-400 leading-none">
                  {net.toLocaleString('ru-RU')}
                </span>
                <span className="text-xl text-emerald-500 pb-0.5">₽</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-black/20 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-slate-600 mb-0.5">В день</p>
              <p className="text-sm font-semibold text-white">{perDay.toLocaleString('ru-RU')} ₽</p>
            </div>
            <div className="bg-black/20 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-slate-600 mb-0.5">За год</p>
              <p className="text-sm font-semibold text-white">{(net * 12).toLocaleString('ru-RU')} ₽</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info block */}
      <div className="flex items-start gap-3 bg-[#13161f] border border-[#1e2130] rounded-2xl px-5 py-4">
        <span className="w-5 h-5 flex items-center justify-center text-slate-600 flex-shrink-0 mt-0.5">
          <i className="ri-scales-line text-sm" />
        </span>
        <p className="text-xs text-slate-600 leading-relaxed">
          Расчёт является приблизительным прогнозом на основе средних показателей платформ.
          Ставка <strong className="text-slate-500">5 ₽ за отклик</strong>. Налог НПД 6% взимается с каждого поступления.
          Среднее время работы в сутки — около 20 минут (скачать отклики и загрузить сюда).
        </p>
      </div>
    </div>
  );
}
